import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createPlatformGlobalBanner,
  createPlatformTenantBanner,
  deletePlatformGlobalBanner,
  deletePlatformTenantBanner,
  getPlatformGlobalBanner,
  getPlatformTenantBanner,
  listPlatformGlobalBanners,
  listPlatformTenantBanners,
  updatePlatformGlobalBanner,
  updatePlatformTenantBanner
} from '../services/platformAdminClient';
import '../styles/PlatformBannerManager.css';

const MAX_SOURCE_BYTES = 8 * 1024 * 1024;
const MAX_EMBEDDED_LENGTH = 100_000;
const ACCEPTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const GLOBAL_SCOPE = '__all_tenants__';

const EMPTY_FORM = Object.freeze({
  titulo: '',
  descricao: '',
  tipo: 'Publicidade',
  parceiro: '',
  imagem_url: '',
  imagem_base64: '',
  cta_texto: 'Conhecer oferta',
  link: '',
  prioridade: '0',
  abrir_nova_aba: true,
  ativo: true,
  data_inicio: '',
  data_fim: ''
});

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Não foi possível ler a imagem selecionada.'));
    reader.readAsDataURL(file);
  });
}

function loadImage(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('O arquivo selecionado não contém uma imagem válida.'));
    image.src = source;
  });
}

export async function compressPlatformBannerImage(file) {
  if (!file || !ACCEPTED_IMAGE_TYPES.has(String(file.type || '').toLowerCase())) {
    throw new Error('Envie uma imagem JPG, PNG ou WebP.');
  }
  if (file.size > MAX_SOURCE_BYTES) {
    throw new Error('A imagem original deve ter no máximo 8 MB.');
  }

  const original = await readFileAsDataUrl(file);
  const image = await loadImage(original);
  const initialScale = Math.min(1, 1600 / image.naturalWidth, 520 / image.naturalHeight);
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Este navegador não permite preparar a imagem para envio.');

  const scales = [1, 0.9, 0.8, 0.7, 0.6, 0.5, 0.42];
  const qualities = [0.86, 0.76, 0.66, 0.56, 0.46, 0.36];
  let smallest = '';
  for (const scale of scales) {
    canvas.width = Math.max(1, Math.round(image.naturalWidth * initialScale * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * initialScale * scale));
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    for (const quality of qualities) {
      const candidate = canvas.toDataURL('image/webp', quality);
      if (!smallest || candidate.length < smallest.length) smallest = candidate;
      if (candidate.length <= MAX_EMBEDDED_LENGTH) return candidate;
    }
  }
  if (smallest && smallest.length <= MAX_EMBEDDED_LENGTH) return smallest;
  throw new Error('Não foi possível reduzir a imagem ao limite do banner. Use uma peça mais simples ou uma URL externa.');
}

function nullable(value) {
  const normalized = String(value == null ? '' : value).trim();
  return normalized || null;
}

function bannerPayload(form) {
  return {
    titulo: String(form.titulo || '').trim(),
    descricao: nullable(form.descricao),
    tipo: nullable(form.tipo) || 'Publicidade',
    parceiro: nullable(form.parceiro),
    imagem_url: nullable(form.imagem_url),
    imagem_base64: nullable(form.imagem_base64),
    cta_texto: nullable(form.cta_texto),
    link: nullable(form.link),
    prioridade: Number(form.prioridade || 0),
    abrir_nova_aba: Boolean(form.abrir_nova_aba),
    ativo: Boolean(form.ativo),
    data_inicio: nullable(form.data_inicio),
    data_fim: nullable(form.data_fim)
  };
}

function bannerForm(banner = {}) {
  return {
    titulo: banner.titulo || '',
    descricao: banner.descricao || '',
    tipo: banner.tipo || 'Publicidade',
    parceiro: banner.parceiro || '',
    imagem_url: banner.imagem_url || '',
    imagem_base64: banner.imagem_base64 || '',
    cta_texto: banner.cta_texto || 'Conhecer oferta',
    link: banner.link || '',
    prioridade: String(banner.prioridade || 0),
    abrir_nova_aba: [true, 1, '1', 'true'].includes(banner.abrir_nova_aba),
    ativo: ![false, 0, '0', 'false'].includes(banner.ativo),
    data_inicio: banner.data_inicio || '',
    data_fim: banner.data_fim || ''
  };
}

function tenantName(tenant) {
  return tenant?.displayName || tenant?.display_name || tenant?.name || tenant?.slug || tenant?.id || '';
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function periodLabel(banner) {
  const start = banner.data_inicio;
  const end = banner.data_fim;
  const reference = today();
  if (start && start > reference) return `Agendado para ${start}`;
  if (end && end < reference) return `Encerrado em ${end}`;
  if (start || end) return `${start || 'início livre'} a ${end || 'sem término'}`;
  return 'Sem período definido';
}

function Feedback({ value, onDismiss }) {
  if (!value?.text) return null;
  return (
    <div className={`platform-feedback is-${value.type || 'info'}`} role={value.type === 'error' ? 'alert' : 'status'}>
      <i className={`fas ${value.type === 'error' ? 'fa-circle-exclamation' : value.type === 'success' ? 'fa-circle-check' : 'fa-circle-info'}`} aria-hidden="true" />
      <span>{value.text}</span>
      <button type="button" onClick={onDismiss} aria-label="Fechar aviso"><i className="fas fa-xmark" aria-hidden="true" /></button>
    </div>
  );
}

export default function PlatformBannerManager({ tenants = [], loadingTenants = false, refreshToken = 0 }) {
  const [tenantId, setTenantId] = useState('');
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM }));

  useEffect(() => {
    if (tenantId === GLOBAL_SCOPE) return;
    if (tenantId && tenants.some((tenant) => tenant.id === tenantId)) return;
    setTenantId(tenants[0]?.id || GLOBAL_SCOPE);
  }, [tenantId, tenants]);

  const globalScope = tenantId === GLOBAL_SCOPE;

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === tenantId) || null,
    [tenantId, tenants]
  );

  const load = useCallback(async () => {
    if (!tenantId) {
      setBanners([]);
      return;
    }
    setLoading(true);
    setFeedback(null);
    try {
      const result = globalScope
        ? await listPlatformGlobalBanners()
        : await listPlatformTenantBanners(tenantId);
      setBanners(Array.isArray(result?.items) ? result.items : []);
    } catch (error) {
      setBanners([]);
      setFeedback({ type: 'error', text: error.message || 'Não foi possível carregar os banners.' });
    } finally {
      setLoading(false);
    }
  }, [globalScope, tenantId]);

  useEffect(() => { void load(); }, [load, refreshToken]);

  const resetEditor = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setShowForm(false);
  };

  const startCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setFeedback(null);
    setShowForm(true);
  };

  const startEdit = async (banner) => {
    setBusy(`edit-${banner.id}`);
    setFeedback(null);
    try {
      const result = globalScope
        ? await getPlatformGlobalBanner(banner.id)
        : await getPlatformTenantBanner(tenantId, banner.id);
      const detail = result?.banner;
      if (!detail) throw new Error('Banner não encontrado.');
      setEditing({ id: banner.id, version: detail.version || banner.version });
      setForm(bannerForm(detail));
      setShowForm(true);
    } catch (error) {
      setFeedback({ type: 'error', text: error.message || 'Não foi possível abrir o banner.' });
    } finally {
      setBusy('');
    }
  };

  const change = (event) => {
    const { name, value, checked, type } = event.target;
    setForm((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy('upload');
    setFeedback({ type: 'info', text: 'Preparando a imagem para o banner...' });
    try {
      const image = await compressPlatformBannerImage(file);
      setForm((current) => ({ ...current, imagem_base64: image, imagem_url: '' }));
      setFeedback({ type: 'success', text: 'Imagem compactada e pronta para envio.' });
    } catch (error) {
      setFeedback({ type: 'error', text: error.message });
    } finally {
      setBusy('');
    }
  };

  const save = async (event) => {
    event.preventDefault();
    if (!String(form.titulo || '').trim()) {
      setFeedback({ type: 'error', text: 'Informe o título do banner.' });
      return;
    }
    if (form.data_inicio && form.data_fim && form.data_inicio > form.data_fim) {
      setFeedback({ type: 'error', text: 'A data final não pode anteceder a data inicial.' });
      return;
    }
    setBusy('save');
    setFeedback(null);
    try {
      if (editing && globalScope) {
        await updatePlatformGlobalBanner(editing.id, editing.version, bannerPayload(form));
      } else if (editing) {
        await updatePlatformTenantBanner(tenantId, editing.id, editing.version, bannerPayload(form));
      } else if (globalScope) {
        await createPlatformGlobalBanner(bannerPayload(form));
      } else {
        await createPlatformTenantBanner(tenantId, bannerPayload(form));
      }
      resetEditor();
      await load();
      setFeedback({
        type: 'success',
        text: editing
          ? `Banner atualizado ${globalScope ? 'para todos os tenants' : 'na home do tenant'}.`
          : `Banner publicado ${globalScope ? 'para todos os tenants' : 'na home do tenant'}.`
      });
    } catch (error) {
      setFeedback({ type: 'error', text: error.message || 'Não foi possível salvar o banner.' });
    } finally {
      setBusy('');
    }
  };

  const toggle = async (banner) => {
    setBusy(`toggle-${banner.id}`);
    setFeedback(null);
    try {
      const result = globalScope
        ? await getPlatformGlobalBanner(banner.id)
        : await getPlatformTenantBanner(tenantId, banner.id);
      const detail = result?.banner;
      if (!detail) throw new Error('Banner não encontrado.');
      const activate = Number(banner.ativo || 0) !== 1;
      const payload = { ...bannerPayload(bannerForm(detail)), ativo: activate };
      if (globalScope) {
        await updatePlatformGlobalBanner(banner.id, detail.version || banner.version, payload);
      } else {
        await updatePlatformTenantBanner(tenantId, banner.id, detail.version || banner.version, payload);
      }
      await load();
      setFeedback({ type: 'success', text: activate ? 'Banner ativado.' : 'Banner pausado.' });
    } catch (error) {
      setFeedback({ type: 'error', text: error.message || 'Não foi possível alterar o banner.' });
    } finally {
      setBusy('');
    }
  };

  const remove = async (banner) => {
    const destination = globalScope ? 'de todos os tenants' : 'deste tenant';
    if (!window.confirm(`Excluir o banner “${banner.titulo}” ${destination}?`)) return;
    setBusy(`delete-${banner.id}`);
    setFeedback(null);
    try {
      if (globalScope) {
        await deletePlatformGlobalBanner(banner.id, banner.version);
      } else {
        await deletePlatformTenantBanner(tenantId, banner.id, banner.version);
      }
      if (editing?.id === banner.id) resetEditor();
      await load();
      setFeedback({ type: 'success', text: `Banner excluído ${destination}.` });
    } catch (error) {
      setFeedback({ type: 'error', text: error.message || 'Não foi possível excluir o banner.' });
    } finally {
      setBusy('');
    }
  };

  const activeCount = banners.filter((banner) => Number(banner.ativo || 0) === 1).length;
  const preview = form.imagem_base64 || form.imagem_url;

  return (
    <section className="platform-banner-manager" aria-busy={loading || Boolean(busy)}>
      <article className="platform-card platform-banner-context">
        <header>
          <div><span className="platform-eyebrow">Destino da campanha</span><h2>{globalScope ? 'Home pós-login de todos os tenants' : 'Home pós-login do tenant'}</h2></div>
          <button type="button" className="platform-button is-primary" onClick={startCreate} disabled={!tenantId || loadingTenants}>
            <i className="fas fa-plus" aria-hidden="true" /> Novo banner
          </button>
        </header>
        <div className="platform-banner-tenant-picker">
          <label htmlFor="platform-banner-tenant"><span>Público que receberá o banner</span>
            <select id="platform-banner-tenant" value={tenantId} onChange={(event) => { resetEditor(); setTenantId(event.target.value); }} disabled={loadingTenants}>
              <option value={GLOBAL_SCOPE}>Todos os tenants · campanha global</option>
              {tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenantName(tenant)} · {tenant.slug || tenant.id}</option>)}
            </select>
          </label>
          <div><strong>{banners.length}</strong><span>cadastrados</span></div>
          <div><strong>{activeCount}</strong><span>ativos</span></div>
          <p><i className="fas fa-circle-info" aria-hidden="true" /> {globalScope
            ? 'A campanha global aparece para tenants atuais e futuros; campanhas individuais continuam isoladas.'
            : 'A campanha é isolada por empresa e aparece no carrossel comercial da página inicial.'}</p>
        </div>
      </article>

      <Feedback value={feedback} onDismiss={() => setFeedback(null)} />

      {showForm && (
        <article className="platform-card platform-banner-editor">
          <header><div><span className="platform-eyebrow">{editing ? 'Edição versionada' : 'Nova campanha'}</span><h2>{editing ? 'Editar banner comercial' : 'Criar banner comercial'}</h2></div><button type="button" className="platform-icon-button" onClick={resetEditor} aria-label="Fechar formulário"><i className="fas fa-xmark" /></button></header>
          <form className="platform-editor-form" onSubmit={save}>
            <p className="platform-editor-context"><strong>{globalScope ? 'Todos os tenants · atuais e futuros' : selectedTenant ? tenantName(selectedTenant) : 'Tenant não selecionado'}</strong><span>Use preferencialmente uma peça horizontal de 1600 × 420 px. O upload é compactado automaticamente e não é gravado no log de auditoria.</span></p>
            <div className="platform-form-grid">
              <label><span>Título *</span><input name="titulo" value={form.titulo} onChange={change} maxLength="180" required /></label>
              <label><span>Parceiro ou anunciante</span><input name="parceiro" value={form.parceiro} onChange={change} maxLength="180" /></label>
              <label><span>Tipo</span><input name="tipo" value={form.tipo} onChange={change} maxLength="80" /></label>
              <label><span>Prioridade</span><input type="number" name="prioridade" min="-9999" max="9999" value={form.prioridade} onChange={change} /></label>
              <label className="is-full"><span>Descrição</span><textarea name="descricao" value={form.descricao} onChange={change} maxLength="1200" /></label>
              <label><span>Upload da imagem</span><input type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} disabled={busy === 'upload'} /><small>JPG, PNG ou WebP, até 8 MB.</small></label>
              <label><span>Ou URL da imagem</span><input name="imagem_url" value={form.imagem_url} onChange={(event) => setForm((current) => ({ ...current, imagem_url: event.target.value, imagem_base64: event.target.value ? '' : current.imagem_base64 }))} maxLength="2048" placeholder="https://..." /></label>
              {preview && <div className="platform-banner-preview is-full"><img src={preview} alt={`Prévia de ${form.titulo || 'banner comercial'}`} /><button type="button" onClick={() => setForm((current) => ({ ...current, imagem_url: '', imagem_base64: '' }))}><i className="fas fa-trash" /> Remover imagem</button></div>}
              <label><span>Texto do botão</span><input name="cta_texto" value={form.cta_texto} onChange={change} maxLength="100" /></label>
              <label><span>Link de destino</span><input name="link" value={form.link} onChange={change} maxLength="2048" placeholder="https://..." /></label>
              <label><span>Início da vigência</span><input type="date" name="data_inicio" value={form.data_inicio} onChange={change} /></label>
              <label><span>Fim da vigência</span><input type="date" name="data_fim" value={form.data_fim} onChange={change} /></label>
              <label className="platform-checkbox"><input type="checkbox" name="abrir_nova_aba" checked={form.abrir_nova_aba} onChange={change} /> Abrir link em nova aba</label>
              <label className="platform-checkbox"><input type="checkbox" name="ativo" checked={form.ativo} onChange={change} /> Publicar como ativo</label>
            </div>
            <div className="platform-modal-actions"><button type="button" className="platform-button is-ghost" onClick={resetEditor} disabled={busy === 'save'}>Cancelar</button><button type="submit" className="platform-button is-primary" disabled={busy === 'save' || busy === 'upload'}><i className={`fas ${busy === 'save' ? 'fa-spinner fa-spin' : 'fa-cloud-arrow-up'}`} /> {editing ? 'Salvar alterações' : 'Publicar banner'}</button></div>
          </form>
        </article>
      )}

      <article className="platform-card platform-banner-list">
        <header><div><span className="platform-eyebrow">{globalScope ? 'Campanhas globais' : 'Campanhas do tenant'}</span><h2>{globalScope ? 'Todos os tenants' : selectedTenant ? tenantName(selectedTenant) : 'Selecione uma empresa'}</h2></div><small>{activeCount} ativo(s) de {banners.length}</small></header>
        {loading ? <div className="platform-section-loading" role="status"><i className="fas fa-spinner fa-spin" /> Carregando banners...</div> : !tenantId ? (
          <div className="platform-empty-state"><i className="fas fa-building-circle-xmark" /><h3>Nenhum tenant disponível</h3><p>Cadastre ou carregue uma empresa para publicar campanhas.</p></div>
        ) : !banners.length ? (
          <div className="platform-empty-state"><i className="fas fa-rectangle-ad" /><h3>Nenhum banner cadastrado</h3><p>{globalScope ? 'Crie a primeira campanha comercial para todos os tenants.' : 'Crie a primeira campanha comercial para esta empresa.'}</p></div>
        ) : (
          <div className="platform-banner-items">
            {banners.map((banner) => {
              const active = Number(banner.ativo || 0) === 1;
              return (
                <article key={banner.id}>
                  <span className="platform-banner-thumb">{banner.imagem_url ? <img src={banner.imagem_url} alt="" /> : <i className={`fas ${banner.tem_imagem_base64 ? 'fa-image' : 'fa-rectangle-ad'}`} aria-hidden="true" />}</span>
                  <div className="platform-banner-copy"><strong>{banner.titulo}</strong><span>{banner.parceiro || banner.tipo || 'Publicidade'}</span><small>{periodLabel(banner)} · prioridade {banner.prioridade || 0}</small></div>
                  <span className={`platform-status ${active ? 'is-active' : 'is-inactive'}`}>{active ? 'Ativo' : 'Pausado'}</span>
                  <div className="platform-banner-actions">
                    <button type="button" onClick={() => void startEdit(banner)} disabled={Boolean(busy)} aria-label={`Editar ${banner.titulo}`} title="Editar"><i className="fas fa-pen" /></button>
                    <button type="button" onClick={() => void toggle(banner)} disabled={Boolean(busy)} aria-label={`${active ? 'Pausar' : 'Ativar'} ${banner.titulo}`} title={active ? 'Pausar' : 'Ativar'}><i className={`fas ${active ? 'fa-pause' : 'fa-play'}`} /></button>
                    <button type="button" className="is-danger" onClick={() => void remove(banner)} disabled={Boolean(busy)} aria-label={`Excluir ${banner.titulo}`} title="Excluir"><i className="fas fa-trash" /></button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </article>
    </section>
  );
}
